import { useCallback, useEffect, useRef, useState } from 'react';

export type ScrollDirection = 'up' | 'down' | null;

const useScrollDirection = (threshold = 10): ScrollDirection => {
  const [direction, setDirection] = useState<ScrollDirection>(null);
  const lastScrollY = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
  const ticking = useRef(false);

  const update = useCallback(() => {
    const scrollLocked = getComputedStyle(document.body).overflow === 'hidden';

    if (scrollLocked) {
      ticking.current = false;
      return;
    }

    const currentScrollY = window.scrollY;

    if (currentScrollY === 0) {
      setDirection(null);
      lastScrollY.current = 0;
      ticking.current = false;
      return;
    }

    const diff = currentScrollY - lastScrollY.current;
    if (Math.abs(diff) >= threshold) {
      setDirection(diff > 0 ? 'down' : 'up');
      lastScrollY.current = currentScrollY;
    }
    ticking.current = false;
  }, [threshold]);

  useEffect(() => {
    lastScrollY.current = window.scrollY;
    const onScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(update);
        ticking.current = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [update]);

  return direction;
};

export default useScrollDirection;
